import { PropertyFilter } from '@fullstory/proto/fs/services/dx-objects';
import { UpdateVarsRequest, VarRule } from '@fullstory/proto/fs/services/fieldmeta';
import { useNotifications } from '@fullstory/react-hooks';
import {
  AppBackendRequestActions,
  AppDispatch,
  fullstorySelectors,
  SearchMetaRequestActions,
} from '@fullstory/redux';
import { FieldScope } from '@fullstory/search-vars';
import { assertExhaustive } from '@fullstory/typeutils';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { PropertyRow } from '../types';

function toVarType(t: PropertyRow['propertyType']): VarRule.VarType {
  switch (t) {
    case FieldScope.User:
      return VarRule.VarType.VAR_TYPE_USER_PROPERTY;
    case FieldScope.Page:
      return VarRule.VarType.VAR_TYPE_PAGE_PROPERTY;
    case FieldScope.Event:
      return VarRule.VarType.VAR_TYPE_EVENT_PROPERTY;
    case FieldScope.Element:
      return VarRule.VarType.VAR_TYPE_ELEMENT_PROPERTY;
    case FieldScope.Fsevt:
      return VarRule.VarType.VAR_TYPE_INVALID;
    case FieldScope.NoScope:
      return VarRule.VarType.VAR_TYPE_INVALID;
    default:
      assertExhaustive(t, `unhandled property type: ${t}`);
  }
}

export const usePropertyActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const intl = useIntl();
  const notifications = useNotifications();

  return {
    archiveProperties: React.useCallback(
      async (properties: PropertyRow[], archive: boolean, connectionParent?: string | number) => {
        const updates: UpdateVarsRequest.VarUpdate[] = [];
        for (const prop of properties) {
          updates.push({
            type: toVarType(prop.propertyType),
            pattern: prop.id,
            parentPattern: connectionParent ? `${connectionParent}` : '',
            state: archive
              ? VarRule.VarState.VAR_STATE_ARCHIVED
              : VarRule.VarState.VAR_STATE_INVALID,
            applyBefore: null,
          });
          if (connectionParent && !archive) {
            // if a connection has been provided for unarchive, we need to make sure to also unarchive the
            // global property for use (this is safe even if a global rule doesn't exist)
            updates.push({
              type: toVarType(prop.propertyType),
              pattern: prop.id,
              parentPattern: '',
              state: VarRule.VarState.VAR_STATE_INVALID,
              applyBefore: null,
            });
          }
        }

        const count = properties.length;
        try {
          await dispatch(SearchMetaRequestActions.updateVarRules(orgId, updates));
          if (count === 1) {
            notifications.success(
              intl.formatMessage(
                {
                  defaultMessage: '{isArchive, select, true {Archived} other {Restored}} Property.',
                  id: 'IBXvfz',
                },
                { isArchive: archive },
              ),
            );
          } else {
            notifications.success(
              intl.formatMessage(
                {
                  defaultMessage:
                    '{isArchive, select, true {Archived} other {Restored}} {count, number} Properties.',
                  id: 'zC8Ze1',
                },
                { isArchive: archive, count },
              ),
            );
          }
        } catch (e) {
          notifications.error(
            intl.formatMessage(
              {
                defaultMessage:
                  'Failed to {isArchive, select, true {archive} other {restore}} {count, plural, one {Property} other {Properties}}.',
                id: '+JqTzC',
              },
              { isArchive: archive, count },
            ),
          );
        }
      },
      [dispatch, intl, notifications, orgId],
    ),
    archiveAllProperties: React.useCallback(
      async (filters: PropertyFilter[], query: string, numRows: number, archive: boolean) => {
        try {
          await dispatch(
            AppBackendRequestActions.bulkArchiveProperties({
              archive,
              orgId,
              filters,
              query,
              lastUpdatedBefore: null,
            }),
          );
          notifications.success(
            intl.formatMessage(
              {
                defaultMessage:
                  '{isArchive, select, true {Archived} other {Restored}} {count, plural, one {Property} other {Properties}}',
                id: 'hqQVQf',
              },
              { isArchive: archive, count: numRows },
            ),
          );
        } catch (e) {
          notifications.error(
            intl.formatMessage(
              {
                defaultMessage:
                  'Failed to {isArchive, select, true {archive} other {restore}} {count, plural, one {Property} other {Properties}}.',
                id: '+JqTzC',
              },
              { isArchive: archive, count: numRows },
            ),
          );
        }
      },
      [dispatch, intl, notifications, orgId],
    ),
  };
};
